// Salesforce Connection Flow Alpine.js Component
// Handles 4-step connection process with state management

document.addEventListener('alpine:init', () => {
  Alpine.data('salesforce_connect_data', () => ({
    // Current step state (1-4)
    currentStep: 1,
    
    // Step completion tracking
    steps: {
      credentials: false,
      mapping: false,
      testing: false,
      validated: false
    },
    
    // Loading states
    loading: {
      connect: false,
      test: false
    },
    
    // Form data for Step 1
    form: {
      instance_url: '',
      consumer_key: '',
      consumer_secret: '',
      username: '',
      password: '',
      security_token: '',
      sync_frequency: ''
    },
    
    // Error messages
    errors: {
      credentials: '',
      test: ''
    },
    
    // Success messages
    success: {
      credentials: false,
      mapping: false,
      test: false
    },
    
    // Test record information
    test_record_info: null,
    
    // Salesforce URL
    salesforce_url: '',
    
    init() {
      // Load integration state from server if exists
      const stateData = this.$refs.integration_state?.dataset.state;
      if (stateData) {
        const state = JSON.parse(stateData);
        this.loadState(state);
      }
      
      // Set initial step as open
      this.expandStep(this.currentStep);
    },
    
    // Load existing state from server
    loadState(state) {
      if (state.current_step) {
        this.currentStep = state.current_step;
      }
      
      if (state.steps_completed) {
        this.steps = { ...this.steps, ...state.steps_completed };
      }
      
      if (state.form_data) {
        this.form = { ...this.form, ...state.form_data };
      }
      
      if (state.test_record_info) {
        this.test_record_info = state.test_record_info;
      }
      
      if (state.salesforce_url) {
        this.salesforce_url = state.salesforce_url;
      }
    },
    
    // Check if a step can be accessed
    canAccessStep(stepNumber) {
      if (stepNumber === 1) return true;
      if (stepNumber === 2) return this.steps.credentials;
      if (stepNumber === 3) return this.steps.credentials && this.steps.mapping;
      if (stepNumber === 4) return this.steps.credentials && this.steps.mapping && this.steps.testing;
      return false;
    },
    
    // Get step status icon
    getStepIcon(stepNumber) {
      if (this.isStepComplete(stepNumber)) {
        return 'complete';
      } else if (this.currentStep === stepNumber) {
        return 'current';
      } else if (this.canAccessStep(stepNumber)) {
        return 'available';
      }
      return 'locked';
    },
    
    // Check if step is complete
    isStepComplete(stepNumber) {
      const stepMap = {
        1: 'credentials',
        2: 'mapping',
        3: 'testing',
        4: 'validated'
      };
      return this.steps[stepMap[stepNumber]] || false;
    },
    
    // Expand/collapse step
    toggleStep(stepNumber) {
      if (this.canAccessStep(stepNumber)) {
        this.currentStep = this.currentStep === stepNumber ? 0 : stepNumber;
      }
    },
    
    // Expand specific step
    expandStep(stepNumber) {
      if (this.canAccessStep(stepNumber)) {
        this.currentStep = stepNumber;
      }
    },
    
    // Check if step is expanded
    isStepExpanded(stepNumber) {
      return this.currentStep === stepNumber;
    },
    
    // Validate Step 1 form
    validateCredentialsForm() {
      this.errors.credentials = '';
      
      if (!this.form.instance_url.trim()) {
        this.errors.credentials = 'Salesforce Instance URL is required';
        return false;
      }
      
      if (!this.form.consumer_key.trim()) {
        this.errors.credentials = 'Consumer Key is required';
        return false;
      }
      
      if (!this.form.consumer_secret.trim()) {
        this.errors.credentials = 'Consumer Secret is required';
        return false;
      }
      
      if (!this.form.username.trim()) {
        this.errors.credentials = 'Salesforce Username is required';
        return false;
      }
      
      if (!this.form.password.trim()) {
        this.errors.credentials = 'Salesforce Password is required';
        return false;
      }
      
      if (!this.form.security_token.trim()) {
        this.errors.credentials = 'Security Token is required';
        return false;
      }
      
      if (!this.form.sync_frequency) {
        this.errors.credentials = 'Sync Frequency is required';
        return false;
      }
      
      // Basic URL validation
      try {
        const url = new URL(this.form.instance_url);
        if (!url.hostname.includes('salesforce.com')) {
          this.errors.credentials = 'Please enter a valid Salesforce instance URL';
          return false;
        }
      } catch (e) {
        this.errors.credentials = 'Please enter a valid URL';
        return false;
      }
      
      return true;
    },
    
    // Step 1: Save credentials and verify connection
    async saveCredentials() {
      if (!this.validateCredentialsForm()) {
        return;
      }
      
      this.loading.connect = true;
      this.errors.credentials = '';
      
      try {
        const token = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
        const showId = document.getElementById('salesforce-connect-container')?.dataset.showId;
        
        const response = await fetch(`/shows/${showId}/integrations/salesforce/save_credentials`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': token
          },
          body: JSON.stringify({
            instance_url: this.form.instance_url,
            consumer_key: this.form.consumer_key,
            consumer_secret: this.form.consumer_secret,
            username: this.form.username,
            password: this.form.password,
            security_token: this.form.security_token,
            sync_frequency: this.form.sync_frequency
          })
        });
        
        const data = await response.json();
        
        if (data.success) {
          this.steps.credentials = true;
          this.success.credentials = true;
          this.currentStep = 2;
          
          // Show success toast
          this.showToast('Credentials saved successfully!', 'success');
        } else {
          this.errors.credentials = data.error || 'Failed to save credentials';
        }
      } catch (error) {
        console.error('Error saving credentials:', error);
        this.errors.credentials = 'An unexpected error occurred. Please try again.';
      } finally {
        this.loading.connect = false;
      }
    },
    
    // Step 2: Mark mapping as complete
    async completeMappingStep() {
      try {
        const token = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
        const showId = document.getElementById('salesforce-connect-container')?.dataset.showId;
        
        const response = await fetch(`/shows/${showId}/integrations/salesforce/complete_mapping`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': token
          }
        });
        
        const data = await response.json();
        
        if (data.success) {
          this.steps.mapping = true;
          this.success.mapping = true;
          this.currentStep = 3;
          this.showToast('Mapping step completed!', 'success');
        }
      } catch (error) {
        console.error('Error completing mapping:', error);
      }
    },
    
    // Step 3: Test data sync
    async testDataSync() {
      this.loading.test = true;
      this.errors.test = '';
      
      try {
        const token = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
        const showId = document.getElementById('salesforce-connect-container')?.dataset.showId;
        
        const response = await fetch(`/shows/${showId}/integrations/salesforce/test_sync`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': token
          }
        });
        
        const data = await response.json();
        
        if (data.success) {
          this.steps.testing = true;
          this.success.test = true;
          this.currentStep = 4;
          
          // Store test record info if provided
          if (data.record_info) {
            this.test_record_info = data.record_info;
          }
          
          this.showToast('Test sync successful!', 'success');
        } else {
          // Show helpful message for common errors
          if (data.error && data.error.includes('The requested resource does not exist')) {
            this.errors.test = 'Test data failed to sync. Please ensure the custom objects and all its fields are correctly set up in Salesforce.';
          } else {
            this.errors.test = data.error || 'Test data failed to sync. Please ensure the custom objects and all its fields are correctly set up in Salesforce.';
          }
        }
      } catch (error) {
        console.error('Error testing sync:', error);
        this.errors.test = 'An unexpected error occurred during test sync.';
      } finally {
        this.loading.test = false;
      }
    },
    
    // Cancel and return to integrations
    cancel() {
      const showId = document.getElementById('salesforce-connect-container')?.dataset.showId;
      if (showId) {
        window.location.href = `/shows/${showId}/integrations`;
      }
    },
    
    // Show toast notification (placeholder - integrate with existing toast system)
    showToast(message, type = 'success') {
      // This should integrate with the existing Rails flash/toast system
      console.log(`[${type.toUpperCase()}] ${message}`);
    },
    
    // Get sync frequency description
    getSyncFrequencyDescription() {
      const descriptions = {
        'daily': 'Occurs every day. Data will be sent for the previous day.',
        'weekly': 'Occurs every Monday. Data will be sent for the previous week.',
        'monthly': 'Occurs every 1st of the month. Data will be sent for the previous calendar month.'
      };
      return descriptions[this.form.sync_frequency] || '';
    }
  }));
});

